import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, ChevronDown, LogOut, Globe } from 'lucide-react';
import { Avatar, NotificationBell, NOTIFICATION_TYPES } from '@clareefai/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useGetVendorMeQuery } from '../../store/vendorApi';
import { teamMemberApi } from '../../store/teamMemberApi';

interface HeaderProps {
  onToggleSidebar: () => void;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isTeamMember, firebaseUser } = useAuth();
  const { data: vendor } = useGetVendorMeQuery(undefined, { skip: !!isTeamMember });
  const { data: teamMemberProfile } = teamMemberApi.useGetMyProfileQuery(undefined, {
    skip: !isTeamMember,
  });
  const { t, i18n } = useTranslation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Close the profile menu whenever the route changes (sidebar nav, notifications, etc.)
  const lastPath = useRef(location.pathname);
  if (lastPath.current !== location.pathname) {
    lastPath.current = location.pathname;
    if (showProfileMenu) setShowProfileMenu(false);
  }

  const tm = teamMemberProfile ?? user?.teamMember;

  const fullName = isTeamMember
    ? ((tm?.name ?? [tm?.firstName, tm?.lastName].filter(Boolean).join(' ')) || user?.name || '')
    : (vendor ? [vendor.firstName, vendor.lastName].filter(Boolean).join(' ') : (user?.name ?? ''));
  const email = isTeamMember ? (tm?.email ?? user?.username ?? '') : (vendor?.email ?? user?.email ?? '');
  const company = isTeamMember
    ? (teamMemberProfile?.companyName ?? '')
    : (vendor?.companyName ?? vendor?.name ?? '');
  const logo = isTeamMember ? teamMemberProfile?.companyLogo : vendor?.logo;
  const avatar = isTeamMember ? (tm?.avatar ?? user?.avatar) : (vendor?.avatar ?? vendor?.logo ?? user?.avatar);

  return (
    <header className="header flex items-center justify-between">
      <button
        onClick={onToggleSidebar}
        className="md:hidden p-2 rounded-lg hover:bg-gray-100"
      >
        <Menu className="w-6 h-6" />
      </button>

      <div className="flex-1 max-w-2xl mx-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={t('header.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="flex items-center space-x-4">
        {(company || logo) && (
          <div className="flex items-center space-x-3 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
            <Avatar
              src={logo || undefined}
              name={company || undefined}
              size="sm"
              className="h-8 w-8 rounded-lg object-contain flex-shrink-0"
            />
            {company && (
              <span className="text-sm font-semibold text-gray-700 truncate max-w-[140px]" title={company}>
                {company}
              </span>
            )}
          </div>
        )}

        <div className="hidden md:block w-px h-8 bg-gray-300"></div>

        <NotificationBell
          collectionPath="vendors"
          firebaseUid={firebaseUser?.uid ?? null}
          appName="Clareefai Vendor"
          onNotificationClick={(n) => {
            switch (n.type) {
              case NOTIFICATION_TYPES.MEETING_SCHEDULED:
              case NOTIFICATION_TYPES.MEETING_ENDED:
              case NOTIFICATION_TYPES.MEETING_COMING_SOON:
                navigate('/meetings');
                break;
              case NOTIFICATION_TYPES.NEW_PROMOTER_VERIFIED:
                navigate('/promoters');
                break;
              case NOTIFICATION_TYPES.TESTIMONIAL_SUBMITTED:
              case NOTIFICATION_TYPES.REVIEW_PUBLISHED:
                if (n.testimonialType === 'use case') {
                  navigate('/use-cases');
                } else {
                  navigate('/reviews');
                }
                break;
              case NOTIFICATION_TYPES.NEW_PROSPECT_VERIFIED:
              case NOTIFICATION_TYPES.PROSPECT_FINALIZED:
                navigate('/prospects');
                break;
              case NOTIFICATION_TYPES.SPECIFIC_REWARD_COMPLETED:
              case NOTIFICATION_TYPES.SPECIFIC_REWARD_CLAIMED:
                navigate('/rewards');
                break;
              default:
                navigate('/dashboard');
            }
          }}
          renderNotification={(notification, defaultContent) => (
            <>
              {defaultContent}
              {notification.feedback && (
                <div className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-700">
                  {String(notification.feedback)}
                </div>
              )}
            </>
          )}
        />

        <div className="relative">
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Avatar
              src={avatar}
              name={fullName || undefined}
              firstName={isTeamMember ? tm?.firstName : vendor?.firstName}
              lastName={isTeamMember ? tm?.lastName : vendor?.lastName}
              size="sm"
              className="border-2 border-white shadow-sm"
            />
            <span className="hidden md:block text-sm font-medium text-gray-700">{fullName || user?.name}</span>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </button>

          {showProfileMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900">{fullName || '—'}</p>
                <p className="text-xs text-gray-500">{email || '—'}</p>
                {company && <p className="text-xs text-purple-600 mt-1">{company}</p>}
              </div>
              
              <div className="px-4 py-2 border-b border-gray-100">
                <div className="flex items-center space-x-2">
                  <Avatar
                    src={logo || undefined}
                    name={company || undefined}
                    size="sm"
                    className="rounded flex-shrink-0 object-contain"
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{company || 'Company'}</p>
                    <p className="text-xs text-gray-500">Client Organization</p>
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 border-t border-gray-100">
                <p className="flex items-center text-xs font-medium text-gray-500 mb-2">
                  <Globe className="w-3.5 h-3.5 mr-1.5" />
                  {t('header.language')}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => i18n.changeLanguage('en')}
                    className={`flex-1 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                      i18n.resolvedLanguage === 'en'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    English
                  </button>
                  <button
                    type="button"
                    onClick={() => i18n.changeLanguage('fr')}
                    className={`flex-1 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                      i18n.resolvedLanguage === 'fr'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Français
                  </button>
                </div>
              </div>

              <button
                onClick={() => {
                  logout();
                  setShowProfileMenu(false);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('header.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
